
import { DeepAnalysisResult, ShareabilityScores, AudienceScore, ExpertInsight, LegacyBonusItem } from '@/types/analysisTypes';

/**
 * Parse the response from OpenAI into a structured DeepAnalysisResult
 */
export function parseOpenAIResponse(responseText: string): DeepAnalysisResult {
  try {
    // Parse the JSON response
    const result = JSON.parse(responseText) as DeepAnalysisResult;
    
    // Validate and normalize the structure
    if (!result.overall_score) {
      result.overall_score = 0;
    }
    
    // Ensure category_scores exists with all required fields
    if (!result.category_scores) {
      result.category_scores = {
        content_quality: 0,
        EEAT: 0,
        helpfulness: 0,
        shareability: 0,
        ai_optimization_score: 0
      };
    }
    
    // Clean rebuild: Pass category_scores unmodified
    console.log('🚨 responseParser - category_scores keys:', Object.keys(result.category_scores));
    
    // Map legacy fields to expected structure if needed
    mapLegacyFields(result);
    
    return result;
  } catch (error) {
    console.error('Error parsing OpenAI response:', error);
    return createErrorResult(`Failed to parse OpenAI response: ${(error as Error).message}`);
  }
}

/**
 * Create an error result object
 */
export function createErrorResult(errorMessage: string): DeepAnalysisResult {
  return {
    error: errorMessage,
    overall_score: 0,
    category_scores: {
      content_quality: 0,
      EEAT: 0,
      helpfulness: 0,
      shareability: 0,
      ai_optimization_score: 0
    },
    actionable_fixes: [],
    shareability_scores: {
      beginner: { score: 0, explanation: '', improvement: '' },
      intermediate: { score: 0, explanation: '', improvement: '' },
      expert: { score: 0, explanation: '', improvement: '' }
    },
    bonus_section: {
      expert_insight: { 
        title: 'Error Occurred',
        intro: errorMessage,
        key_takeaways: ['Analysis encountered an error', 'Please try again later'],
        conclusion: 'Contact support if this issue persists'
      },
      unexpected_analogy: {
        title: '',
        intro: '',
        key_points: [],
        conclusion: ''
      },
      challenged_assumption: {
        title: '',
        intro: '',
        key_reasons: [],
        solution: ''
      },
      insider_tip: {
        title: '',
        intro: '',
        step_by_step: [],
        final_takeaway: ''
      }
    }
  };
}

/**
 * Map legacy fields to the expected structure
 */
function mapLegacyFields(result: DeepAnalysisResult): void {
  if (typeof result.shareability_scores === 'number') {
    // Convert the number to the new object format
    const legacyScore = result.shareability_scores;
    result.shareability_scores = {
      beginner: { score: legacyScore, explanation: '', improvement: '' },
      intermediate: { score: legacyScore, explanation: '', improvement: '' },
      expert: { score: legacyScore, explanation: '', improvement: '' }
    };
  } else if (result.shareability_scores && typeof result.shareability_scores === 'object') {
    // Handle the case where shareability_scores is an object but contains number values
    const shareabilityScores = result.shareability_scores as ShareabilityScores;
    for (const key in shareabilityScores) {
      if (typeof shareabilityScores[key] === 'number') {
        shareabilityScores[key] = {
          score: shareabilityScores[key] as number,
          explanation: '',
          improvement: ''
        } as AudienceScore;
      }
    }
    result.shareability_scores = shareabilityScores;
  }
  
  // Bonus Section Migration
  if (result.bonus_section && typeof result.bonus_section === 'object') {
    const bonusSection = result.bonus_section as any; // Use 'any' to access properties dynamically
    
    // Migrate expert_insight
    if (bonusSection.expert_insight && bonusSection.expert_insight.question) {
      const legacyItem = bonusSection.expert_insight as LegacyBonusItem;
      result.bonus_section.expert_insight = {
        title: 'Expert Insight',
        intro: legacyItem.question || '',
        key_takeaways: [legacyItem.example || ''],
        conclusion: ''
      } as ExpertInsight;
    }
    
    // Migrate unexpected_analogy
    if (bonusSection.unexpected_analogy && bonusSection.unexpected_analogy.question) {
      const legacyItem = bonusSection.unexpected_analogy as LegacyBonusItem;
      result.bonus_section.unexpected_analogy = {
        title: 'Unexpected Analogy',
        intro: legacyItem.question || '',
        key_points: [legacyItem.example || ''],
        conclusion: ''
      };
    }
    
    // Migrate challenged_assumption
    if (bonusSection.challenged_assumption && bonusSection.challenged_assumption.question) {
      const legacyItem = bonusSection.challenged_assumption as LegacyBonusItem;
      result.bonus_section.challenged_assumption = {
        title: 'Challenged Assumption',
        intro: legacyItem.question || '',
        key_reasons: [legacyItem.example || ''],
        solution: ''
      };
    }
    
    // Migrate insider_tip
    if (bonusSection.insider_tip && bonusSection.insider_tip.question) {
      const legacyItem = bonusSection.insider_tip as LegacyBonusItem;
      result.bonus_section.insider_tip = {
        title: 'Insider Tip',
        intro: legacyItem.question || '',
        step_by_step: [legacyItem.example || ''],
        final_takeaway: ''
      };
    }
  }
}
